<script setup lang="ts">
import { computed } from 'vue';
import MarqueeComponent from '@/components/contents/interim/components/MarqueeComponent.vue';

type ImageEntry = string | { src: string; className?: string };

interface StoryblokAsset {
    filename?: string;
    alt?: string;
}

interface LogoBarBlok {
    _uid: string;
    logos?: StoryblokAsset[];
    background_color?: 'peach' | 'white' | 'light' | 'warm';
    speed?: 'slow' | 'normal' | 'fast';
}

const props = defineProps<{ blok: LogoBarBlok }>();

/** Same partner strip as HomePageRedesignContent / former PageBlok injection. */
const defaultPartners: ImageEntry[] = [
    '/images/redesign/slider/adam-eve.png',
    '/images/redesign/slider/dripdrip.png',
    { src: '/images/redesign/slider/liquidrom.png', className: 'h-6' },
    '/images/redesign/slider/reformer-club.png',
    '/images/redesign/slider/soulhouse.png',
    '/images/redesign/slider/weleda-spa.png',
];

const backgroundClass = computed(() => {
    switch (props.blok.background_color) {
        case 'white':
            return 'bg-white';
        case 'light':
            return 'bg-[#FCF7F4]';
        case 'warm':
            return 'bg-[#FCEEE6]';
        case 'peach':
        default:
            return 'bg-[#FBDBCA]';
    }
});

const durationSeconds = computed(() => {
    switch (props.blok.speed) {
        case 'slow':
            return 45;
        case 'fast':
            return 20;
        case 'normal':
        default:
            return 30;
    }
});

const images = computed((): ImageEntry[] => {
    const raw = props.blok.logos;
    const list = Array.isArray(raw) ? raw : [];
    const fromCms = list
        .map((a) => {
            const filename = a?.filename;
            if (!filename) {
                return null;
            }
            const isStoryblok = filename.includes('storyblok.com');
            const src = isStoryblok ? `${filename}/m/400x0` : filename;

            return { src, className: 'h-10' } as ImageEntry;
        })
        .filter((x): x is ImageEntry => x !== null);

    return fromCms.length > 0 ? fromCms : defaultPartners;
});
</script>

<template>
    <div v-editable="blok" :class="backgroundClass">
        <MarqueeComponent :images="images" :duration-seconds="durationSeconds" />
    </div>
</template>
